import React, { useEffect, useState } from "react";
import { Grid, Box, AppBar, Tab, Tabs } from "@mui/material";
import Header from "../Header/Header";
import managementService from "../../services/AdminManagementService";
import CustomerAndSiteSelector from "../Common/CustomerAndSiteSelector";
import { SiteIntegrationTypeEnum } from "@beable/roster-backend-client-v1";
import {
  ORAPIClientConfigDTO,
  SiteDTO,
} from "@beable/roster-backend-client-v1";
import {
  CustomerActionType,
  useCustomerDispatch,
} from "../../context/CustomerContext";
import { CustomerSetupStore } from "./RosterSource/RosterSource.store";
import BasePanel from "../Common/Layout/BasePanel";
import { BasePanelBox } from "../Common/Layout/BasePanelBox";
import BaseSheet from "../Common/Layout/BaseSheet";
import { StyledPanelHeader } from "../Common/Layout/PanelHeader";
import { SiteTypeManagement } from "./SiteType/SiteTypeManagement";
import { RosterSourceManagement } from "./RosterSource/RosterSourceManagement";
import { PasswordManagement } from "./PasswordControl/PasswordManagement";
import { PauseDates } from "./PauseDates/PauseDates";
import { ContentAreaManagement } from "./ContentAreas/ContentAreaManagement";
import { SiteBatchSteps } from "./BatchSteps/BatchSteps";

const siteInitialValue: SiteDTO = new SiteDTO();
const providerInitialValue: ORAPIClientConfigDTO = {
  id: "",
  applicationId: "",
  accessToken: "",
  endpointUrl: "",
  siteId: 0,
  provider: "",
  scheduledDays: "",
  scheduledHour: "",
  disableSchedule: true,
};

const customerSetupStore = new CustomerSetupStore();

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function tabProps(index: any) {
  return {
    id: `customer-setup-tab-${index}`,
    "aria-controls": `customer-setup-tabpanel-${index}`,
  };
}

export default function CustomerSetup() {
  const [site, setSite] = useState<SiteDTO>(siteInitialValue);
  const [orapiProvider, setORAPIProvider] = useState(providerInitialValue);
  const customerDispatch: any = useCustomerDispatch();
  const [value, setValue] = React.useState(0);
  const [messageSettings, setMessageSettings] = useState({
    open: false,
    message: "",
    severity: "info" as 'error' | 'info' | 'success' | 'warning',
  });
  const [initalIntegrationType, setInitialIntegrationType] = useState<SiteIntegrationTypeEnum>(SiteIntegrationTypeEnum.OneRosterCSV);

  const handleChangeTabs = (event: React.SyntheticEvent<Element, Event>, newValue: number) => {
    setValue(newValue);
  };

  const handleSiteChange = (site: SiteDTO) => {
    customerSetupStore.previousIntegrationType = undefined;
    if (site) {
      setSite(site);
      setInitialIntegrationType(site.integrationType ?? SiteIntegrationTypeEnum.OneRosterCSV);
      customerDispatch({
        type: CustomerActionType.SiteChanged,
        site,
      });
    } else {
      setSite(siteInitialValue);
    }
    setORAPIProvider(providerInitialValue);
  };

  useEffect(() => {
    if (!site) return;
    if (site) {
      if (SiteIntegrationTypeEnum.OneRosterCSV === site.integrationType) return;
      if (customerSetupStore.previousIntegrationType) return;
    }
    const getData = async () => {
      if (site && site.id && !orapiProvider.siteId) {
        const response = await managementService.getAPIAccessData(site.id);
        // if we have previously configured data stored in the database
        if (response) {
          customerSetupStore.savedSettings = response;
          setORAPIProvider({ ...response });
        } else {
          setORAPIProvider({ ...providerInitialValue, siteId: site.id });
        }
      }
    };
    getData();
  }, [orapiProvider.siteId, site]);

  const showMessage = (msg: string, severity: 'error' | 'info' | 'success' | 'warning') => {
    setMessageSettings({ open: true, message: msg, severity });
  };

  const closeMessage = () => {
    setMessageSettings({
      open: false,
      message: "",
      severity: messageSettings.severity,
    });
  };

  return (
    <>
      <Header />
      <Grid container>
        <BasePanel>
          <BasePanelBox>
            <StyledPanelHeader header="Customer Setup" />
            <Grid container size={12}>
              <CustomerAndSiteSelector onSiteSelect={handleSiteChange} />
            </Grid>
          </BasePanelBox>
        </BasePanel>
        <BaseSheet widerPaper>
          {site && (
            <div>
              <AppBar position="static">
                <Tabs
                  value={value}
                  onChange={handleChangeTabs}
                  aria-label="customerSetupTabs"
                  textColor="inherit"
                  indicatorColor='secondary'
                  scrollButtons='auto'
                  variant="scrollable"
                >
                  <Tab label="Site Type" {...tabProps(0)} />
                  <Tab label="Roster Source" {...tabProps(1)} />
                  <Tab label="Batch Steps" {...tabProps(2)} />
                  <Tab label="Password Control" {...tabProps(3)} />
                  <Tab label="Pause Dates" {...tabProps(4)} />
                  <Tab label="Content Areas" {...tabProps(5)} />
                </Tabs>
              </AppBar>
              <TabPanel value={value} index={0}>
                <SiteTypeManagement
                  site={site}
                  showMessage={showMessage}
                  closeMessage={closeMessage}
                  messageSettings={messageSettings}
                  setSite={setSite}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <RosterSourceManagement
                  setORAPIProvider={setORAPIProvider}
                  orapiProvider={orapiProvider}
                  setSite={setSite}
                  site={site}
                  showMessage={showMessage}
                  closeMessage={closeMessage}
                  messageSettings={messageSettings}
                  initalIntegrationType={initalIntegrationType}
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <SiteBatchSteps site={site} />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <PasswordManagement
                  site={site}
                  showMessage={showMessage}
                  closeMessage={closeMessage}
                  messageSettings={messageSettings}
                  setSite={setSite}
                />
              </TabPanel>
              <TabPanel value={value} index={4}>
                <PauseDates siteId={site.id} />
              </TabPanel>
              <TabPanel value={value} index={5}>
                <ContentAreaManagement
                  site={site}
                  showMessage={showMessage}
                  closeMessage={closeMessage}
                  messageSettings={messageSettings}
                />
              </TabPanel>
            </div>
          )}
        </BaseSheet>
      </Grid>
    </>
  );
}
